import { createRegexMatcher } from './matchers/regex-matcher';

export type Matcher = (input: string) => number;

export type TokenDef<TType> = {
    readonly matcher: Matcher;
    readonly type: TType;
};

export type Token<TType> = {
    readonly type: TType;
    readonly position: number;
    readonly content: string;
};

type MatchedToken<TTokenType> = {
    readonly tokenDefinition: TokenDef<TTokenType>;
    readonly content: string;
};

export const tokenize = <TTokenType>(tokensDefinitions: TokenDef<TTokenType>[], input: string): Token<TTokenType>[] => {
    return tokenize(tokensDefinitions, input, 0);

    function tokenize(tokensDefinitions: TokenDef<TTokenType>[], input: string, position: number): Token<TTokenType>[] {
        if (!input)
            return [];
        const matchedToken = findMatch(tokensDefinitions, input);
        if (matchedToken) {
            return [
                createToken(matchedToken, position),
                ...tokenize(tokensDefinitions, input.substr(matchedToken.content.length), position + matchedToken.content.length)
            ];
        } else if (matchWhitespace(input)) {
            return tokenize(tokensDefinitions, input.substr(1), position + 1);
        }
        throw new Error(`Invalid token at position: ${position}.`);
    }
};

const findMatch = <TTokenType>(tokensDefinitions: TokenDef<TTokenType>[], input: string): MatchedToken<TTokenType> | null => {
    for (const def of tokensDefinitions) {
        const matchedCharactersCount = def.matcher(input);
        if (matchedCharactersCount > 0) {
            return {
                tokenDefinition: def,
                content: input.substr(0, matchedCharactersCount)
            };
        }
    }
    return null;
};

const createToken = <TTokenType>(matchedToken: MatchedToken<TTokenType>, position: number): Token<TTokenType> => ({
    content: matchedToken.content,
    position,
    type: matchedToken.tokenDefinition.type
});

const matchWhitespace = createRegexMatcher(/^\s/);
